import React, { useState, useEffect } from "react";
import "./PopupDataSource.scss";
import {
  createDataSource,
  deleteDataSource,
  updateDataSource,
  getDataSourceById,
} from "../../api/dataSourceService";
// Quitamos ToastContainer, Toastify CSS e importamos React Hot Toast:
import { toast } from "react-hot-toast";
import { fetchDashboardData } from "../../redux/actions/dashboardActions";
import { useDispatch } from "react-redux";


const GPS_LAT = "gps_lat_parsed"
const GPS_LON = "gps_lon_parsed"
const SPEED = "speed_parsed"
const THROTTLE = "throttle_parsed"
const BRAKE = "brake_parsed"
const STEERING = "steering_parsed"

const PopupDataSource = ({ datos, selectedData, onClose, tit }) => {
  const [vehicleName, setVehicleName] = useState("");
  const [gpsLongitude, setGpsLongitude] = useState("");
  const [gpsLatitude, setGpsLatitude] = useState("");
  const [speed, setSpeed] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [throttleName, setThrottleName] = useState("");
  const [brakeName, setBrakeName] = useState("");
  const [steeringName, setSteeringName] = useState("");

  // Estados originales para comparar cambios
  const [originalVehicleName, setOriginalVehicleName] = useState("");
  const [originalGpsLongitude, setOriginalGpsLongitude] = useState("");
  const [originalGpsLatitude, setOriginalGpsLatitude] = useState("");
  const [originalSpeed, setOriginalSpeed] = useState("");
  const [originalSourceType, setOriginalSourceType] = useState("");
  const [originalThrottleName, setOriginalThrottleName] = useState("");
  const [originalBrakeName, setOriginalBrakeName] = useState("");
  const [originalSteeringName, setOriginalSteeringName] = useState("");

  const options = Object.keys(datos.dashboardData.parsers);
  const dispatch = useDispatch();


  // Obtener datos de la fuente (si se está editando)
  const getDataSource = async () => {
    try {
      const data = await getDataSourceById(selectedData.id);
      const channelNames = data.channel_names || {};

      // Función para encontrar la key según el valor
      const getKeyByValue = (object, value) =>
        Object.keys(object).find((key) => object[key] === value) || "";

      const gpsLatitudeKey = getKeyByValue(channelNames, GPS_LAT);
      const gpsLongitudeKey = getKeyByValue(channelNames, GPS_LON);
      const speedKey = getKeyByValue(channelNames, SPEED);
      const throttleKey = getKeyByValue(channelNames, THROTTLE);
      const brakeKey = getKeyByValue(channelNames, BRAKE);
      const steeringKey = getKeyByValue(channelNames, STEERING);

      setVehicleName(data.vehicle_name || "");
      setGpsLongitude(gpsLongitudeKey || "");
      setGpsLatitude(gpsLatitudeKey || "");
      setSpeed(speedKey || "");
      setSourceType(data.source_type || "");
      setThrottleName(throttleKey || "");
      setBrakeName(brakeKey || "");
      setSteeringName(steeringKey || "");

      // Guardar valores originales para luego comparar
      setOriginalVehicleName(data.vehicle_name || "");
      setOriginalGpsLongitude(gpsLongitudeKey || "");
      setOriginalGpsLatitude(gpsLatitudeKey || "");
      setOriginalSpeed(speedKey || "");
      setOriginalSourceType(data.source_type || "");
      setOriginalThrottleName(throttleKey || "");
      setOriginalBrakeName(brakeKey || "");
      setOriginalSteeringName(steeringKey || "");
    } catch (error) {
      console.error("Error al obtener la fuente de datos:", error);
    }
  };

  // Cargar datos si se está editando
  useEffect(() => {
    if (tit === "Edit data source" && selectedData) {
      getDataSource();
    }
  }, [tit, selectedData]);

  // Crear fuente de datos
  const onSave = async () => {
    if (
      !vehicleName ||
      !gpsLongitude ||
      !gpsLatitude ||
      !speed ||
      !sourceType ||
      !throttleName ||
      !brakeName
    ) {
      toast.error("All fields must be filled!", { position: "top-center" });
      return;
    }

    const channelNames = {
      [gpsLatitude]: GPS_LAT,
      [gpsLongitude]: GPS_LON,
      [speed]: SPEED,
      [throttleName]: THROTTLE,
      [brakeName]: BRAKE,
    };
    if (steeringName) {
      channelNames[steeringName] = STEERING;
    }

    try {
      await createDataSource(channelNames, sourceType, vehicleName);
      dispatch(fetchDashboardData());
      toast.success("Data source created successfully!", {
        position: "top-center",
      });
      onClose();
    } catch (error) {
      toast.error("Failed to create data source. Please try again.", {
        position: "top-center",
      });
      console.error("Error creating data source:", error);
    }
  };

  // Actualizar fuente de datos
  const onUpdate = async () => {
    // Verificar si hubo cambios
    if (
      vehicleName === originalVehicleName &&
      gpsLongitude === originalGpsLongitude &&
      gpsLatitude === originalGpsLatitude &&
      speed === originalSpeed &&
      sourceType === originalSourceType &&
      throttleName === originalThrottleName &&
      brakeName === originalBrakeName &&
      steeringName === originalSteeringName
    ) {
      toast("No changes detected, skipping update.", {
        position: "top-center",
      });
      return;
    }

    const channelNames = {
      [gpsLatitude]: GPS_LAT,
      [gpsLongitude]: GPS_LON,
      [speed]: SPEED,
      [throttleName]: THROTTLE,
      [brakeName]: BRAKE,
    };
    if (steeringName) {
      channelNames[steeringName] = STEERING;
    }

    try {
      await updateDataSource(
        selectedData.id,
        channelNames,
        sourceType,
        vehicleName,
      );
      toast.success("Data source updated successfully!", {
        position: "top-center",
      });
      dispatch(fetchDashboardData());
      onClose();
    } catch (error) {
      toast.error("Failed to update data source. Please try again.", {
        position: "top-center",
      });
      console.error("Error updating data source:", error);
    }
  };

  // Eliminar fuente de datos
  const onDelete = async () => {
    try {
      await deleteDataSource(selectedData.id);
      toast.success("Data source deleted successfully!", {
        position: "top-center",
      });
      dispatch(fetchDashboardData());
      onClose();
    } catch (error) {
      toast.error("Failed to delete data source. Please try again.", {
        position: "top-center",
      });
      console.error("Error deleting data source:", error);
    }
  };

  return (
    <div className="popup">
      <div className="popup-content popup-data-source">
        <div className="tit-popup">
          <h2>{tit}</h2>
        </div>
        <h3>Parameters</h3>
        <div className="data-source">
          <div className="data-source-container">
            <div className="input-container">
              <span className="fixed-text">Vehicle Name</span>
              <input
                type="text"
                placeholder="Data Source name here..."
                value={vehicleName}
                onChange={(e) => setVehicleName(e.target.value)}
              />
            </div>
            <div className="input-container">
              <span className="fixed-text">GPS Longitude</span>
              <input
                type="text"
                placeholder="Channel name..."
                value={gpsLongitude}
                onChange={(e) => setGpsLongitude(e.target.value)}
              />
            </div>
            <div className="input-container">
              <span className="fixed-text">GPS Latitude</span>
              <input
                type="text"
                placeholder="Channel name..."
                value={gpsLatitude}
                onChange={(e) => setGpsLatitude(e.target.value)}
              />
            </div>
            <div className="input-container">
              <span className="fixed-text">Speed</span>
              <input
                type="text"
                placeholder="Channel name..."
                value={speed}
                onChange={(e) => setSpeed(e.target.value)}
              />
            </div>
          </div>
          <div className="data-source-container">
            <div className="input-container">
              <span className="fixed-text">Source Type</span>
              <select
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value)}
                className="source-type-select"
              >
                <option value="" className="default" selected disabled hidden>
                  Select a type
                </option>
                {options.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="input-container">
              <span className="fixed-text">Throttle Name</span>
              <input
                type="text"
                placeholder="Channel name..."
                value={throttleName}
                onChange={(e) => setThrottleName(e.target.value)}
              />
            </div>
            <div className="input-container">
              <span className="fixed-text">Brake Name</span>
              <input
                type="text"
                placeholder="Channel name..."
                value={brakeName}
                onChange={(e) => setBrakeName(e.target.value)}
              />
            </div>
            <div className="input-container">
              <span className="fixed-text">Steering Name</span>
              <input
                type="text"
                placeholder="Channel name..."
                value={steeringName}
                onChange={(e) => setSteeringName(e.target.value)}
              />
            </div>
            <div className="cont-btn">
              {tit !== "Edit data source" && (
                // <button onClick={onSave}>Save</button>
                <button className="btn btn-blue" onClick={onSave}>
                  Add
                </button>
              )}
              {tit === "Edit data source" && (
                // <button onClick={onUpdate}>Update</button>
                <button className="btn btn-blue" onClick={onUpdate}>
                  Edit
                </button>
              )}
              {tit === "Edit data source" && (
                <button className="btn btn-red" onClick={onDelete}>
                  Delete
                </button>
              )}
              <button className="btn" onClick={onClose} >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopupDataSource;
